import { DestroyRef, Signal, signal } from '@angular/core';
import { takeUntilDestroyed } from '@angular/core/rxjs-interop';
import { AbstractControl } from '@angular/forms';

export interface FormChangeTracker {
  /** Call after patching the form with server-loaded values to reset dirty state. */
  markInitial(): void;
  /** True when the current raw value differs from the initial snapshot. */
  readonly hasChanges: Signal<boolean>;
  /** Reactive mirror of `form.valid` — safe to use inside `computed()`. */
  readonly isValid: Signal<boolean>;
}

export function createFormChangeTracker(
  form: AbstractControl,
  destroyRef: DestroyRef
): FormChangeTracker {
  const hasChanges = signal(false);
  const isValid = signal(form.valid);
  let initialSnapshot = '';

  form.statusChanges
    .pipe(takeUntilDestroyed(destroyRef))
    .subscribe(() => isValid.set(form.valid));

  form.valueChanges.pipe(takeUntilDestroyed(destroyRef)).subscribe(() => {
    if (initialSnapshot !== '') {
      hasChanges.set(JSON.stringify(form.getRawValue()) !== initialSnapshot);
    }
  });

  return {
    markInitial(): void {
      initialSnapshot = JSON.stringify(form.getRawValue());
      isValid.set(form.valid);
      hasChanges.set(false);
    },
    hasChanges: hasChanges.asReadonly(),
    isValid: isValid.asReadonly(),
  };
}
